/**
 * Timezone utilities for the timeclock.
 *
 * Storage convention: clock_in / clock_out are stored as UTC ISO-8601
 * instant strings (e.g. "2026-10-07T15:00:00.000Z"), and each punch also
 * records the IANA timezone of the device that made it (e.g. "America/Denver").
 *
 * Display convention: render the instant in the punch's RECORDED timezone so
 * a 9:00 AM punch made in Denver shows as 9:00 AM MDT for every viewer,
 * regardless of where the viewer (or the employee, later) is located.
 * Entries without a recorded timezone (legacy rows) fall back to the
 * viewer's own browser timezone.
 *
 * Everything here is built on the native Intl API — no new dependencies.
 */

export const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

/** IANA timezone of this browser, e.g. "America/Denver". */
export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function isValidTimeZone(tz?: string | null): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function resolveZone(tz?: string | null): string {
  return isValidTimeZone(tz) ? tz : getBrowserTimeZone();
}

function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** "9:05 AM MDT" — time of an instant in the punch's recorded timezone. */
export function formatTimeInZone(iso: string | null | undefined, timeZone?: string | null): string {
  const d = parseInstant(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveZone(timeZone),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(d);
}

/** "Mon, Oct 7" — date of an instant in the punch's recorded timezone. */
export function formatDateInZone(iso: string | null | undefined, timeZone?: string | null): string {
  const d = parseInstant(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveZone(timeZone),
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/** "Oct 7, 2026, 9:05 AM MDT" — full timestamp in the punch's recorded timezone. */
export function formatDateTimeInZone(iso: string | null | undefined, timeZone?: string | null): string {
  const d = parseInstant(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveZone(timeZone),
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(d);
}

/** "10/07/2026" style date key (MM/DD/YYYY) in the given zone, for CSV/PDF. */
export function formatShortDateInZone(iso: string | null | undefined, timeZone?: string | null): string {
  const d = parseInstant(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Wall-clock parts of an instant as seen in a timezone. */
function wallClockParts(utcMs: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(utcMs));

  const map: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = Number(p.value);
  }
  return {
    year: map.year,
    month: map.month,
    day: map.day,
    hour: map.hour % 24,
    minute: map.minute,
    second: map.second,
  };
}

/** Offset (ms) of `timeZone` from UTC at the given instant. */
function getOffsetMs(timeZone: string, utcMs: number): number {
  const p = wallClockParts(utcMs, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - utcMs;
}

/**
 * Convert a wall-clock string ("YYYY-MM-DDTHH:mm", as produced by a
 * datetime-local input) in `timeZone` to a UTC ISO-8601 instant string.
 * Handles DST by resolving the offset in two passes.
 */
export function wallClockToUtcIso(local: string, timeZone: string): string {
  const [datePart, timePart = '00:00'] = local.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  const guessUtc = Date.UTC(y, mo - 1, d, h, mi);
  let utc = guessUtc - getOffsetMs(timeZone, guessUtc);
  const secondPass = guessUtc - getOffsetMs(timeZone, utc);
  if (secondPass !== utc) utc = secondPass;
  return new Date(utc).toISOString();
}

/**
 * Convert a UTC ISO-8601 instant string to a wall-clock string
 * ("YYYY-MM-DDTHH:mm") in `timeZone`, suitable for a datetime-local input.
 */
export function utcIsoToWallClock(iso: string | null | undefined, timeZone: string): string {
  const d = parseInstant(iso);
  if (!d) return '';
  const p = wallClockParts(d.getTime(), resolveZone(timeZone));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}
